import React, { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import * as Sentry from "@sentry/react";
import * as braze from "@braze/web-sdk";
import { AccountExistModal } from "common/AccountExistModal";
import {
  isValidEmail,
  trackLead,
  useQueryParamsAsJson,
  setAnalyticsUserId,
} from "@tomocredit/common-fe/utils";
import { ANALYTICS_EVENTS, LEAD_SOURCES } from "@tomocredit/common-fe/consts";
import { BreakpointContext } from "components/common";
import Email from "common/EmailV2";
import AutoplayCarousel from "common/AutoplayCarousel";
import TestimonialCard1 from "images/landing/testimonial_card-1.webp";
import TestimonialCard2 from "images/landing/testimonial_card-2.webp";
import TestimonialCard3 from "images/landing/testimonial_card-3.webp";
import TestimonialCard4 from "images/landing/testimonial_card-4.webp";
import LandingBanner1 from "images/landing/landing_banner-1.webp";
import LandingBanner2 from "images/landing/landing_banner-2.webp";
import LandingBanner3 from "images/landing/landing_banner-3.webp";
import LandingBanner4 from "images/landing/landing_banner-4.webp";
import Guard from "images/guard.svg";
import IconHeart from "images/heart_icon.svg";
import { socialProofImages } from "./Boost/utils";
import { BureauLogo } from "./Boost/BoostLanding";

const DEFAULT_ERROR_MESSAGE =
  "Could not communicate with server. Please try again later";
const NO_EMAIL_ADDRESS_MESSAGE = "Email address is required";
const INVALID_EMAIL_ADDRESS_MESSAGE = "Please enter a valid eamil address";

const generateLoginLink = (queryParameters) => {
  return `${process.env.REACT_APP_DOMAIN}/dashboard/login${
    queryParameters.size ? `?${queryParameters.toString()}` : ""
  }`;
};

const GetStartedButton = () => {
  return (
    <button
      type="button"
      className="bg-black hover:bg-neutral-600 active:bg-neutral-700 text-white px-4 py-2"
      style={{ borderRadius: 4 }}
      onClick={() => window.scrollTo(0, 0)}
    >
      <span className="font-bold text-md">Get started</span>
    </button>
  );
};

export const TomoLanding = () => {
  const { isBelow } = useContext(BreakpointContext);
  const belowMd = isBelow("md");
  const [error, setError] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [showAccountExistsModal, setShowAccountExistsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { capturedUrlParams, rawUrlParams } = useQueryParamsAsJson(["email"]);

  const history = useHistory();

  const closeModal = () => {
    setShowAccountExistsModal(false);
  };

  const handleSubmit = async (email) => {
    if (loading) {
      return;
    }
    setError("");
    if (!email) {
      setError(NO_EMAIL_ADDRESS_MESSAGE);
      return;
    }
    // simple email check
    if (!isValidEmail(email)) {
      setError(INVALID_EMAIL_ADDRESS_MESSAGE);
      return;
    }
    setUserEmail(email);
    trackLead({ email });

    try {
      setLoading(true);
      const signUpBoostRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/boost/signup`,
        { email, source: LEAD_SOURCES.CARD_LANDING, capturedUrlParams }
      );
      const { userExists, boostEnrolled, guid, softTerminated } = signUpBoostRes?.data ?? {};

      if (guid) {
        braze.changeUser(guid);
        setAnalyticsUserId(guid);
        braze.getUser()?.setEmail(email);
      }

      rawUrlParams.set("email", email);
      if (boostEnrolled && !softTerminated) {
        window.location.assign(generateLoginLink(rawUrlParams));
        return;
      }
      if (!userExists) {
        window?.gtag?.("event", "tomocredit_new_signup", {
          email,
        });
        window.gtag?.("event", ANALYTICS_EVENTS.NEW_LEAD, {
          email,
          source: LEAD_SOURCES.CARD_LANDING,
        });
        window.uetq?.push("event", "lead", {
          event_label: "lead",
          event_category: "lead",
          contact: email,
        });
        if (typeof window.gtag !== "function") {
          Sentry.captureException(
            new Error(
              `New signup detected in ${LEAD_SOURCES.CARD_LANDING} but gtag is not loaded`
            )
          );
        }
      }
      // redirect to boost price page
      history.push(`/boost-pricing?${rawUrlParams.toString()}`);
    } catch (err) {
      console.log(err);
      setError(DEFAULT_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex flex-col md:pt-0 justify-center bg-white leading-normal"
      style={{ letterSpacing: 0.1 }}
    >
      {/* BANNER SECTION */}
      <div className="w-full bg-black text-white">
        <div
          className="mt-10 md:mt-24 flex justify-center gap-6 xl:gap-10 items-center md:flex-row flex-col-reverse px-6 md:px-10 mx-auto"
          style={{ maxWidth: 1440 }}
        >
          <div className="">
            <div className="">
              <h1
                className="mx-2 text-center md:text-left text-4xl md:text-[68px] font-bold md:w-full tracking-tight leading-none"
                style={{ maxWidth: 542 }}
              >
                <span>The World’s First AI Financial Assistant</span>
                <span className="text-primary-txt">.</span>
              </h1>
            </div>
            <div className="flex flex-col-reverse md:flex-col items-center md:items-start text-left self-center w-full whitespace-nowrap">
              <div>
                <div className="self-center md:self-start">
                  <div
                    className="mt-10 md:mt-6 xl:mt-8 flex flex-col lg:flex-row gap-x-4 gap-y-10 box-border"
                    style={{ maxWidth: 507 }}
                  >
                    <Email
                      buttonText="Get started"
                      handleSubmit={handleSubmit}
                      componentClassName="flex-col md:flex-row space-y-2 md:space-y-0"
                      inputClassName="w-full rounded-md md:rounded-r-none border border-neutral-500 md:border-r-0 placeholder-gray-600 text-black bg-white"
                      buttonClassName="bg-primary-txt text-black w-full md:w-auto rounded-md md:rounded-l-none border border-primary-txt font-semibold"
                      loading={loading}
                    />
                  </div>
                  {error && (
                    <p className="text-sm bg-black text-error-200 text-left ml-2 mt-2">
                      {error}
                    </p>
                  )}
                  <div className="mt-6 md:mt-4 text-base flex items-center text-left whitespace-pre-wrap w-full">
                    <img src={Guard} alt="" className="inline mr-2 h-4" />
                    <span>
                      Trusted by <span className="font-bold">4 million+</span>{" "}
                      people, learn credit with Tomo
                    </span>
                  </div>
                </div>
                {showAccountExistsModal && (
                  <AccountExistModal
                    closeModal={closeModal}
                    isOpen={showAccountExistsModal}
                    email={userEmail}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="text-center md:block relative">
            <img
              alt="tomocredit"
              src={LandingBanner1}
              style={{
                width: belowMd ? 320 : 530,
                height: "auto",
                minWidth: belowMd ? 320 : 360,
              }}
            />
            <h3
              className="absolute bottom-0 font-bold text-[10px] md:text-[12px] xl:text-[16px] whitespace-nowrap"
              style={{
                bottom: "10%",
                left: "50%",
                transform: "translate(-50%, 0)",
              }}
            >
              Congrats! Your score went up by 100 points!
            </h3>
          </div>
        </div>
        {/* TESTIMONIAL CARDS SECTION */}
        <div
          className="mt-12 md:mt-24 flex flex-col justify-center items-center mx-auto"
          style={{ maxWidth: 1440 }}
        >
          <div className="font-bold text-2xl text-neutral-500">
            Hear what others have to say
          </div>
          <div className="flex overflow-x-scroll hide-x-scrollbar shrink-0 w-full">
            <div className="mx-6 md:mx-10 mt-5 pb-10 flex self-start overflow-visible">
              {[
                TestimonialCard1,
                TestimonialCard2,
                TestimonialCard3,
                TestimonialCard4,
              ].map((imageSrc, idx) => (
                <img
                  alt=""
                  src={imageSrc}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`testimonial-${idx}`}
                  style={{ width: 295, height: "auto" }}
                  className="pr-6 box-content transform hover:scale-110 transition-transform"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* SOCIAL PROOF CAROUSEL */}
      <div className="py-8 bg-neutral-300">
        <AutoplayCarousel
          titleText="As mentioned in"
          duplicationFactor={3}
          totalWidthPreload={7752} // will need to change this if adding/removing image
          items={socialProofImages.map((item) => {
            return (
              <div className="" style={{ height: 30 }}>
                <img src={item.img} alt={item.alt} className="" />
              </div>
            );
          })}
        />
      </div>
      {/* FEATURES */}
      <div
        className="px-6 md:px-10 mx-auto bg-white text-black"
        style={{ maxWidth: 1440 }}
      >
        <div className="my-10 flex flex-col md:flex-row text-center justify-center items-center gap-4">
          <h2
            className="font-bold text-[28px] text-center leading-tight"
            style={{ letterSpacing: -0.25 }}
          >
            We make your credit journey easy
          </h2>
          <img
            src={IconHeart}
            alt=""
            className="inline"
            style={{ width: 24, height: 23 }}
          />
        </div>
        {/* RAISE CREDIT SCORE */}
        <div
          className="my-10 md:my-0 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-4 box-border mx-auto"
          style={{ height: belowMd ? undefined : 502, maxWidth: 1228 }}
        >
          <div className="flex justify-center items-center order-2">
            <img
              alt=""
              src={LandingBanner2}
              className=""
              style={{
                width: belowMd ? 372 : 521,
                height: "auto",
                objectFit: "cover",
              }}
            />
          </div>
          <div className="flex justify-center items-center order-1">
            <div className="bg-neutral-50 px-6 py-8 md:px-10 md:pt-[60px] md:pb-[45px] flex-1 flex flex-col rounded-lg shrink-0 text-left">
              <div
                className="font-bold leading-tight"
                style={{ fontSize: belowMd ? 28 : 36 }}
              >
                Know your score
                <span className="text-primary-txt">.</span>
              </div>
              <div className="mt-6">
                Stay up-to-date with changes to your latest credit score
              </div>
              <div className="mt-6 h-10 flex">
                <GetStartedButton />
              </div>
            </div>
          </div>
        </div>
        {/* SAFEGUARD CREDIT SCORE */}
        <div
          className="my-10 md:my-0 md:ml-6 xl:ml-12 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-4 box-border mx-auto"
          style={{ height: belowMd ? undefined : 502, maxWidth: 1228 }}
        >
          <div className="flex justify-center items-center order-2 md:order-1">
            <img
              alt=""
              src={LandingBanner3}
              className=""
              style={{
                width: belowMd ? 372 : 521,
                height: "auto",
                objectFit: "cover",
              }}
            />
          </div>
          <div className="flex justify-center items-center order-1 md:order-2">
            <div className="bg-neutral-50 px-6 py-8 md:px-10 md:pt-[60px] md:pb-[45px] flex-1 flex flex-col rounded-lg shrink-0 text-left">
              <div
                className="font-bold leading-tight"
                style={{ fontSize: belowMd ? 28 : 36 }}
              >
                Track Your Bills and Bank Accounts
                <span className="text-primary-txt">.</span>
              </div>
              <div className="mt-6">
                Monitor your real-time spending to maintain and improve the
                health of your personal finance
              </div>
              <div className="mt-6 h-10 flex">
                <GetStartedButton />
              </div>
            </div>
          </div>
        </div>
        {/* AI FINANCIAL ASSISTANT */}
        <div
          className="my-10 md:my-0 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-4 box-border mx-auto"
          style={{ height: belowMd ? undefined : 502, maxWidth: 1228 }}
        >
          <div className="mb-8 flex justify-center items-center order-2">
            <img
              alt=""
              src={LandingBanner4}
              className=""
              style={{
                width: belowMd ? 372 : 521,
                height: "auto",
                objectFit: "cover",
              }}
            />
          </div>
          <div className="flex justify-center items-center order-1">
            <div className="bg-neutral-50 px-6 py-8 md:px-10 md:pt-[60px] md:pb-[45px] flex-1 flex flex-col rounded-lg shrink-0 text-left">
              <div
                className="font-bold leading-tight"
                style={{ fontSize: belowMd ? 28 : 36 }}
              >
                Empower your financial success
                <span className="text-primary-txt">.</span>
              </div>
              <div className="mt-6">
                Chat with our{" "}
                <span className="font-bold">AI financial assistant</span> on any
                financial and credit-related questions. Powered by OpenAI.
              </div>
              <div className="mt-6 h-10 flex">
                <GetStartedButton />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
